/**
 * Clients API Service
 * Handles client/patient management, intake, and lifecycle operations
 */

import { get, post, put, patch, del, upload } from './client';

// =============================================================================
// Client Endpoints
// =============================================================================

/**
 * List clients with optional filtering
 * @param {Object} params - Query parameters
 * @param {string} params.center_id - Filter by clinical center
 * @param {string} params.status - Filter by status
 * @param {string} params.therapist_id - Filter by assigned therapist
 * @param {string} params.search - Search term
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @returns {Promise} Paginated list of clients
 */
export const listClients = (params = {}) => get('/clients', params);

/**
 * Server-side paginated child directory (backs the supervisor Children grid).
 * Unlike listClients — which callers rely on to return the FULL caseload for
 * calendars/pickers — this always paginates and returns real totals.
 * @param {Object} params - { page, page_size, search, center_id, service,
 *   supervisor_id, is_active }
 * @returns {Promise<{items: Array, total: number, page: number, page_size: number, total_pages: number}>}
 */
export const listClientsDirectory = (params = {}) => get('/clients/directory', params);

/**
 * Get client by ID with full details
 * @param {string} clientId - Client UUID
 * @returns {Promise} Client data with programs, assessments
 */
export const getClient = (clientId) => get(`/clients/${clientId}`);

/**
 * Create new client
 * @param {Object} data - Client data
 * @param {string} data.first_name - First name
 * @param {string} data.last_name - Last name
 * @param {string} data.date_of_birth - Date of birth
 * @param {string} data.gender - Gender
 * @param {string} data.center_id - Clinical center ID
 * @returns {Promise} Created client
 */
export const createClient = (data) => post('/clients', data);

/**
 * Update client
 * @param {string} clientId - Client UUID
 * @param {Object} data - Client data to update
 * @returns {Promise} Updated client
 */
export const updateClient = (clientId, data) => put(`/clients/${clientId}`, data);

/**
 * Delete client (soft delete)
 * @param {string} clientId - Client UUID
 * @returns {Promise} Delete response
 */
export const deleteClient = (clientId) => del(`/clients/${clientId}`);

// =============================================================================
// Client Programs
// =============================================================================

/**
 * Get client's active programs
 * @param {string} clientId - Client UUID
 * @returns {Promise} List of programs
 */
export const getClientPrograms = (clientId) => get(`/clients/${clientId}/programs`);

/**
 * Get the client's active IEP targets (not yet mastered, within the active IEP
 * window). Used to show the "Active Targets" count on child cards.
 * @param {string} clientId - Client UUID
 * @returns {Promise<Array>} List of active target rows
 */
export const getClientActiveTargets = (clientId) => get(`/clients/${clientId}/active-targets`);

/**
 * Assign program to client
 * @param {string} clientId - Client UUID
 * @param {Object} data - Program assignment data
 * @param {string} data.program_id - Program ID
 * @param {string} data.start_date - Start date
 * @returns {Promise} Assignment result
 */
export const assignProgram = (clientId, data) => post(`/clients/${clientId}/programs`, data);

// =============================================================================
// Client Guardians
// =============================================================================

/**
 * Get client's guardians/caregivers
 * @param {string} clientId - Client UUID
 * @returns {Promise} List of guardians
 */
export const getClientGuardians = (clientId) => get(`/clients/${clientId}/guardians`);

/**
 * Add guardian to client
 * @param {string} clientId - Client UUID
 * @param {Object} data - Guardian data
 * @returns {Promise} Created guardian relationship
 */
export const addGuardian = (clientId, data) => post(`/clients/${clientId}/guardians`, data);

/**
 * Remove guardian from client
 * @param {string} clientId - Client UUID
 * @param {string} guardianId - Guardian ID
 * @returns {Promise} Removal result
 */
export const removeGuardian = (clientId, guardianId) => del(`/clients/${clientId}/guardians/${guardianId}`);

// =============================================================================
// Client Lifecycle Endpoints
// =============================================================================

/**
 * List client inquiries
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status
 * @param {string} params.center_id - Filter by center
 * @returns {Promise} List of inquiries
 */
export const listInquiries = (params = {}) => get('/client-lifecycle/inquiries', params);

/**
 * List in-progress inquiry drafts (lightweight projection used by the
 * Children page). Excludes converted and closed inquiries server-side.
 * Uses a raw-SQL endpoint that bypasses the partially-stale ClientInquiry
 * SQLAlchemy model.
 * @returns {Promise<{items: Array, total: number}>}
 */
export const listInquiryDrafts = (params = {}) => get('/client-lifecycle/inquiries/drafts', params);

/**
 * Advisory duplicate check to run BEFORE creating a new inquiry. Flags a
 * potential duplicate when an existing (non-deleted) inquiry in the same org and
 * center shares the same child name AND date of birth. Never blocks — the caller
 * decides whether to warn and let the user proceed.
 * @param {Object} params - { center_id, child_first_name, child_last_name, child_date_of_birth, exclude_inquiry_id }
 * @returns {Promise<{is_duplicate: boolean, count: number, matches: Array}>}
 */
export const checkDuplicateInquiry = (params = {}) => get('/client-lifecycle/inquiries/check-duplicate', params);

/**
 * Create new inquiry
 * @param {Object} data - Inquiry data
 * @returns {Promise} Created inquiry
 */
export const createInquiry = (data) => post('/client-lifecycle/inquiries', data);

/**
 * Get inquiry by ID
 * @param {string} inquiryId - Inquiry UUID
 * @returns {Promise} Inquiry data
 */
export const getInquiry = (inquiryId) => get(`/client-lifecycle/inquiries/${inquiryId}`);

/**
 * Update inquiry
 * @param {string} inquiryId - Inquiry UUID
 * @param {Object} data - Inquiry data to update
 * @returns {Promise} Updated inquiry
 */
export const updateInquiry = (inquiryId, data) => put(`/client-lifecycle/inquiries/${inquiryId}`, data);

/**
 * Overwrite an in-progress inquiry (draft) with the full Enquiry-form field set.
 * Use this when RE-saving a resumed draft so the edit updates the same inquiry
 * row instead of creating a duplicate. Takes the SAME payload shape as
 * createInquiry (child_first_name, parent_phone, source, preferred_services, …).
 * @param {string} inquiryId - Inquiry UUID
 * @param {Object} data - Same shape as createInquiry's body
 * @returns {Promise} { id, status: 'updated' }
 */
export const updateInquiryDetails = (inquiryId, data) => put(`/client-lifecycle/inquiries/${inquiryId}/details`, data);

/**
 * Convert inquiry to intake
 * @param {string} inquiryId - Inquiry UUID
 * @returns {Promise} Created intake
 */
export const convertToIntake = (inquiryId) => post(`/client-lifecycle/inquiries/${inquiryId}/convert-to-intake`);

// =============================================================================
// Intake Endpoints
// =============================================================================

/**
 * List intakes
 * @param {Object} params - Query parameters
 * @returns {Promise} List of intakes
 */
export const listIntakes = (params = {}) => get('/client-lifecycle/intakes', params);

/**
 * Get intake by ID
 * @param {string} intakeId - Intake UUID
 * @returns {Promise} Intake data with documents
 */
export const getIntake = (intakeId) => get(`/client-lifecycle/intakes/${intakeId}`);

/**
 * Get aggregated registration status for a child (which forms are completed,
 * percent complete, per-form references). Backs the progress bar on the
 * Child Profiles card and the download buttons on the registration page.
 * @param {string} clientId - Client UUID
 */
export const getClientRegistrationStatus = (clientId) => get(`/client-lifecycle/clients/${clientId}/registration-status`);

/**
 * Update intake
 * @param {string} intakeId - Intake UUID
 * @param {Object} data - Intake data to update
 * @returns {Promise} Updated intake
 */
export const updateIntake = (intakeId, data) => put(`/client-lifecycle/intakes/${intakeId}`, data);

/**
 * Complete intake and create enrollment
 * @param {string} intakeId - Intake UUID
 * @returns {Promise} Created enrollment
 */
export const completeIntake = (intakeId) => post(`/client-lifecycle/intakes/${intakeId}/complete`);

// =============================================================================
// Enrollment Endpoints
// =============================================================================

/**
 * List enrollments
 * @param {Object} params - Query parameters
 * @param {string} params.client_id - Filter by client
 * @param {string} params.program_id - Filter by program
 * @param {string} params.status - Filter by status
 * @returns {Promise} List of enrollments
 */
export const listEnrollments = (params = {}) => get('/client-lifecycle/enrollments', params);

/**
 * Get enrollment by ID
 * @param {string} enrollmentId - Enrollment UUID
 * @returns {Promise} Enrollment data
 */
export const getEnrollment = (enrollmentId) => get(`/client-lifecycle/enrollments/${enrollmentId}`);

/**
 * Update enrollment
 * @param {string} enrollmentId - Enrollment UUID
 * @param {Object} data - Enrollment data to update
 * @returns {Promise} Updated enrollment
 */
export const updateEnrollment = (enrollmentId, data) => put(`/client-lifecycle/enrollments/${enrollmentId}`, data);

/**
 * Discharge client from enrollment
 * @param {string} enrollmentId - Enrollment UUID
 * @param {Object} data - Discharge data
 * @param {string} data.discharge_reason - Reason for discharge
 * @param {string} data.discharge_date - Discharge date
 * @returns {Promise} Discharged enrollment
 */
export const dischargeEnrollment = (enrollmentId, data) => post(`/client-lifecycle/enrollments/${enrollmentId}/discharge`, data);

/**
 * Create a new intake
 * @param {Object} data - Intake data
 * @returns {Promise} Created intake
 */
export const createIntake = (data) => post('/client-lifecycle/intakes', data);

/**
 * Register a new client (creates Person + Client in one call)
 * @param {Object} data - Registration data
 * @param {string} data.first_name - Child first name
 * @param {string} data.last_name - Child last name
 * @param {string} data.org_id - Organization ID
 * @param {string} data.center_id - Center ID (optional)
 * @param {string} data.date_of_birth - Date of birth (optional)
 * @param {string} data.gender - Gender (optional)
 * @param {string} data.inquiry_id - Linked inquiry ID (optional)
 * @param {string} data.intake_id - Linked intake ID (optional)
 * @returns {Promise} Created client with person
 */
export const registerClient = (data) => post('/client-lifecycle/register-client', data);

// =============================================================================
// Caregiver registration (self-service login tied to a child)
// =============================================================================

/**
 * List the caregivers tied to a child, each with their login account status.
 * @param {string} clientId - Client (child) UUID
 * @returns {Promise<Array>} Caregivers with { id, guardian_person_id, user_id,
 *   first_name, last_name, email, phone, relationship_type, is_primary,
 *   is_emergency_contact, account_status }
 */
export const getClientCaregivers = (clientId) => get(`/client-lifecycle/clients/${clientId}/caregivers`);

/**
 * Register a caregiver as a self-service login and tie them to a child.
 * No password is set here — the caregiver receives an invite email and sets
 * their own password (mirrors the user Quick Add's Self Service path).
 * @param {string} clientId - Client (child) UUID
 * @param {Object} data - { email, first_name, last_name, phone?,
 *   relationship_type?, is_primary?, is_emergency_contact?, can_pickup?,
 *   has_legal_custody?, notes? }
 * @returns {Promise} Registration result including invite_sent
 */
export const registerCaregiver = (clientId, data) => post(`/client-lifecycle/clients/${clientId}/register-caregiver`, data);

/**
 * Supervisors allocated to one child - the child's side of
 * clinical.client_supervisor_allocation.
 *
 * The supervisor's side already existed (PUT /users/persons/{id}/supervisor-
 * allocations, "which children does this supervisor hold"), which is the wrong
 * direction when you are looking at a single child during onboarding.
 */
export const listClientSupervisors = (clientId) => get(`/clients/${clientId}/supervisors`);

export const allocateClientSupervisor = (clientId, data) => post(`/clients/${clientId}/supervisors`, data);

export const removeClientSupervisor = (clientId, allocationId) => del(`/clients/${clientId}/supervisors/${allocationId}`);

// =============================================================================
// Registration metadata (Enquiry / Intake / IEP completion + IEP form data)
// =============================================================================

/**
 * Get the registration status + saved IEP form data for a client.
 * @returns {Promise<{ status: { enquiryForm: bool, intakeForm: bool, iep: bool }, complete: bool, iep_data: object | null }>}
 */
export const getClientRegistration = (clientId) => get(`/clients/${clientId}/registration`);

/**
 * Save the full IEP form payload and mark status.iep = true.
 */
export const saveClientIepData = (clientId, iepData) => put(`/clients/${clientId}/registration/iep`, { data: iepData });

/**
 * Save a verbatim camelCase form payload (enquiryForm or intakeForm) on the
 * client and flip its status flag to true. Used in resume mode so re-opening
 * the form from the Registration tab pre-fills every field — including the
 * ones the structured tables don't carry.
 * @param {string} clientId
 * @param {'enquiryForm'|'intakeForm'} formId — the merged id
 *        'enquiryIntakeForm' is still accepted by the API for clients saved
 *        while the two forms were briefly one. Saving either half of such a
 *        client converts it: the API splits the merged blob and stores both.
 *        The intake half is stored WITHOUT the 17 fields the enquiry owns.
 * @param {Object} data Camel-cased form blob
 */
/**
 * Read a filled Child Intake Form PDF and return the values it contains.
 *
 * Extraction ONLY - the API saves nothing. The caller reviews the proposed values
 * and the ordinary save path is what writes them, so an import can never create or
 * alter a child's record on its own.
 *
 * @param {File} file - the PDF
 * @param {Object} [opts]
 * @param {'document_intelligence'|'pdf_text'} [opts.prefer] - force an extractor
 * @returns {Promise} { fields, field_count, provider, pages, warnings, unmapped_labels }
 */
// Reading an intake PDF runs OCR (Document Intelligence) and/or a model call on
// the server before it responds, which routinely exceeds the global 30s axios
// timeout. Give it a generous ceiling that covers DI's own polling window plus
// the extraction call, rather than letting the client abort a valid request.
const INTAKE_IMPORT_TIMEOUT_MS = 180000;

/**
 * Which endpoint reads a document for which form. Two routes, not one with a
 * parameter: each form is its own capability in Features and Components, and the
 * server has to name the page code it checks STATICALLY - see
 * client_lifecycle._import_registration_pdf.
 */
const IMPORT_ROUTES = {
  intake: '/client-lifecycle/intake/import',
  enquiry: '/client-lifecycle/enquiry/import'
};

export const importRegistrationPdf = (file, { prefer, form = 'intake' } = {}) => {
  const route = IMPORT_ROUTES[form];
  if (!route) return Promise.reject(new Error(`Unknown import form '${form}'`));
  const body = new FormData();
  body.append('file', file);
  const query = prefer ? `?prefer=${encodeURIComponent(prefer)}` : '';
  return upload(`${route}${query}`, body, null, { timeout: INTAKE_IMPORT_TIMEOUT_MS });
};

/** Back-compatible alias for the intake import. */
export const importIntakePdf = (file, opts = {}) => importRegistrationPdf(file, { ...opts, form: 'intake' });

export const saveClientFormData = (clientId, formId, data) => put(`/clients/${clientId}/registration/form-data/${formId}`, { data });

/**
 * Patch one or more registration status booleans.
 * @param {Object} updates e.g. { iep: true }
 */
export const patchClientRegistrationStatus = (clientId, updates) => patch(`/clients/${clientId}/registration/status`, updates);

// =============================================================================
// Datasheet Preferences
// =============================================================================

export const saveDatasheetPreferences = (clientId, data) => put(`/client-lifecycle/clients/${clientId}/datasheet-preferences`, data);

export const getDatasheetPreferences = (clientId) => get(`/client-lifecycle/clients/${clientId}/datasheet-preferences`);

// =============================================================================
// Enrollment Creation
// =============================================================================

/**
 * Create a new enrollment
 * @param {Object} data - Enrollment data
 * @param {string} data.client_id - Client UUID
 * @param {string} data.org_id - Organization UUID
 * @param {string} data.center_id - Center UUID
 * @param {string} data.service_id - Service UUID
 * @param {string} data.status_id - Status UUID
 * @param {string} data.enrollment_date - Enrollment date
 * @returns {Promise} Created enrollment
 */
export const createEnrollment = (data) => post('/client-lifecycle/enrollments', data);

// =============================================================================
// Therapist Assignment Endpoints
// =============================================================================

/**
 * List therapist assignments for a client
 * @param {string} clientId - Client UUID
 * @returns {Promise} List of therapist assignments
 */
export const getClientTherapists = (clientId) => get(`/clients/${clientId}/therapists`);

/**
 * Assign a therapist to a client
 * @param {string} clientId - Client UUID
 * @param {Object} data - Assignment data
 * @param {string} data.therapist_person_id - Therapist person UUID
 * @param {string} data.assignment_type - Assignment type (primary, secondary)
 * @param {boolean} data.is_primary - Whether this is the primary therapist
 * @returns {Promise} Created assignment
 */
export const assignTherapist = (clientId, data) => post(`/clients/${clientId}/therapists`, data);

/**
 * Remove therapist assignment
 * @param {string} clientId - Client UUID
 * @param {string} assignmentId - Assignment UUID
 * @returns {Promise} Removal result
 */
export const removeTherapistAssignment = (clientId, assignmentId) => del(`/clients/${clientId}/therapists/${assignmentId}`);

// =============================================================================
// Client Lifecycle Summary
// =============================================================================

/**
 * Get full lifecycle data for a client (inquiry + intake + enrollment)
 * @param {string} clientId - Client UUID
 * @returns {Promise} Lifecycle data with inquiries, intakes, enrollments
 */
export const getClientLifecycle = (clientId) => get(`/client-lifecycle/clients/${clientId}/lifecycle`);

// =============================================================================
// Pipeline Analytics
// =============================================================================

/**
 * Get client pipeline statistics
 * @param {Object} params - Query parameters
 * @param {string} params.center_id - Filter by center
 * @param {string} params.start_date - Start date for period
 * @param {string} params.end_date - End date for period
 * @returns {Promise} Pipeline statistics
 */
export const getPipelineStats = (params = {}) => get('/client-lifecycle/pipeline/stats', params);

/**
 * Get conversion rates
 * @param {Object} params - Query parameters
 * @returns {Promise} Conversion rate data
 */
export const getConversionRates = (params = {}) => get('/client-lifecycle/pipeline/conversion-rates', params);

export default {
  // Clients
  listClients,
  listClientsDirectory,
  getClient,
  createClient,
  updateClient,
  deleteClient,
  getClientPrograms,
  getClientActiveTargets,
  assignProgram,
  getClientGuardians,
  addGuardian,
  removeGuardian,
  getClientCaregivers,
  registerCaregiver,
  listClientSupervisors,
  allocateClientSupervisor,
  removeClientSupervisor,
  // Inquiries
  listInquiries,
  listInquiryDrafts,
  createInquiry,
  getInquiry,
  updateInquiry,
  updateInquiryDetails,
  convertToIntake,
  // Intakes
  listIntakes,
  createIntake,
  getIntake,
  updateIntake,
  completeIntake,
  // Client Registration
  registerClient,
  getClientRegistration,
  saveClientIepData,
  saveClientFormData,
  importIntakePdf,
  importRegistrationPdf,
  patchClientRegistrationStatus,
  // Datasheet Preferences
  saveDatasheetPreferences,
  getDatasheetPreferences,
  // Enrollments
  createEnrollment,
  listEnrollments,
  getEnrollment,
  updateEnrollment,
  dischargeEnrollment,
  // Therapist Assignments
  getClientTherapists,
  assignTherapist,
  removeTherapistAssignment,
  // Lifecycle
  getClientLifecycle,
  // Analytics
  getPipelineStats,
  getConversionRates
};
